// Pure derivation of the "which deal is this?" context the Cleanup
// drawer shows for a Processing (transaction) record. Root-safe — imports
// only the pure realty-side helper + the display-name normalizer — so it
// unit-tests in the node pool without dragging DOM-typed web/src/api.ts
// in (same convention as record-links.ts / cleanup-records.ts).
//
// Nate 2026-07-07: opening a realty cleanup issue, the drawer showed the
// empty "no inline preview" stub — "no idea which Solis this is" — so he
// left the OS to dig through ActiveCampaign. This feeds the drawer the
// address, buy/sell side, close date, sale price, household + advisor:
// enough to identify the deal in the 3-second-comprehension window the
// Cleanup UX rules require.

import { normalizeDisplayName } from "../../../shared/lib/normalize-display-name";
import {
  realtySideLabel,
  transactionDisplayNoun,
  type RealtySide,
} from "../../../shared/realty/transaction-side";

// The realty property-address parts carried on a transaction detail
// (transactions.realty_property.address), used as a fallback when the
// pre-joined property_address string isn't populated.
export interface TxnAddressParts {
  line_1: string | null;
  line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

// The subset of TransactionDetail this derivation reads. Kept narrow (not
// the full DOM-typed TransactionDetail) so the module stays root-safe.
export interface TxnContextInput {
  transaction_type: string;
  realty_side: RealtySide | null;
  service_type: string | null;
  property_address: string | null;
  realty_property: { address: TxnAddressParts | null } | null;
  stage: string;
  outcome: "completed" | "fell_through" | "cancelled" | null;
  owner_name: string | null;
  product_metric: number | null;
  product_metric_unit: string | null;
  expected_close_date: string | null;
  actual_close_date: string | null;
  company_display_name: string;
  household_display_name: string;
}

export interface TxnContext {
  /** Product noun — "Listing" / "Home Purchase" for realty, else the
   *  linked service record's product ("Purchase Mortgage", "Home Policy"),
   *  else the humanized transaction_type as a last resort. */
  noun: string;
  side: RealtySide | null;
  /** "Buy-side" / "Sell-side", or null for non-realty. */
  sideLabel: string | null;
  /** One-line property address, or null (non-realty / none on file). */
  property: string | null;
  company: string;
  household: string;
  advisor: string | null;
  /** The stage label, or the closed-outcome label once the deal closed. */
  stageStatus: string;
  isClosed: boolean;
  /** "Closed" once actual_close_date is set, else "Expected close". */
  closeDateLabel: string;
  closeDate: string | null;
  /** "Sale price" for realty, else the humanized product-metric unit. */
  priceLabel: string;
  price: number | null;
}

function humanize(s: string): string {
  return s
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const OUTCOME_LABELS: Record<
  "completed" | "fell_through" | "cancelled",
  string
> = {
  completed: "Completed",
  fell_through: "Fell through",
  cancelled: "Cancelled",
};

// Compose a one-line address from the realty_property parts. Returns null
// when there's no street line — a bare city/zip isn't an address worth
// leading with (matches formatPropertyAddress's "street or nothing" rule).
export function formatTxnAddress(
  parts: TxnAddressParts | null | undefined,
): string | null {
  if (!parts) return null;
  const street = (parts.line_1 ?? "").trim();
  if (!street) return null;
  const cityState = [parts.city, parts.state]
    .map((c) => (c ?? "").trim())
    .filter(Boolean)
    .join(", ");
  const chunks = [street, (parts.line_2 ?? "").trim(), cityState, (parts.zip ?? "").trim()]
    .filter(Boolean);
  return chunks.join(" · ");
}

export function transactionContext(t: TxnContextInput): TxnContext {
  const side = t.realty_side;
  const property =
    (t.property_address ?? "").trim() ||
    formatTxnAddress(t.realty_property?.address ?? null);
  const isClosed = !!(t.actual_close_date ?? "").trim();
  return {
    noun: transactionDisplayNoun(t.transaction_type, side, t.service_type),
    side,
    sideLabel: side ? realtySideLabel(side) : null,
    property: property || null,
    company: t.company_display_name,
    household: t.household_display_name,
    advisor: (t.owner_name ?? "").trim() || null,
    stageStatus: t.outcome ? OUTCOME_LABELS[t.outcome] : humanize(t.stage),
    isClosed,
    closeDateLabel: isClosed ? "Closed" : "Expected close",
    closeDate:
      (isClosed ? t.actual_close_date : t.expected_close_date) || null,
    priceLabel: side
      ? "Sale price"
      : t.product_metric_unit
        ? humanize(t.product_metric_unit)
        : "Value",
    price: t.product_metric,
  };
}

// The minimal member shape needed to render "Robert & Maria Solis" — the
// "which Solis" disambiguator the household display name alone can't give.
export interface TxnMember {
  preferred_name?: string | null;
  legal_first_name?: string | null;
  legal_last_name?: string | null;
}

// Household members' display names, all-caps-normalized (Grant's rule) and
// blank-filtered. Empty array when there are no named members.
export function householdMemberNames(
  members: readonly TxnMember[] | null | undefined,
): string[] {
  return (members ?? [])
    .map((m) => {
      const joined = [
        m.preferred_name ?? m.legal_first_name,
        m.legal_last_name,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
      return normalizeDisplayName(joined) || joined;
    })
    .filter(Boolean);
}
